// Building blocks for AI-generated scenes: shape, text and character layers in the editor's own
// layer format (the same fields the editor writes when a layer is added by hand), and the custom
// SVG shapes (cards, blobs, icons, badges...) the premium templates are made of.
//
// Sizes are canvas pixels of the editor canvas: 524 x 295 for 16:9, 295 x 524 for mobile (9:16)
// and 295 x 295 for square (1:1); the same pixel size in all three, so texts and characters are
// as big in every format. 116 timeline px = 1 second.

/** Canvas of the template being built (set by setCanvas; 16:9 unless another size is chosen). */
export let CANVAS = "16:9";
export let W = 524;
export let H = 295;

/** The saved slide size per canvas (the editor rescales the layers from it). */
const SLIDE_SIZES = {
  "16:9": { w: 524, h: 294.7496315629606, W: 524, H: 295 },
  "9:16": { w: 294.7496315629606, h: 524, W: 295, H: 524 },
  "1:1": { w: 295, h: 295, W: 295, H: 295 },
};

/** Chooses the canvas the next scenes are designed for ("16:9", "9:16" or "1:1"). */
export function setCanvas(size) {
  const c = SLIDE_SIZES[size] ? size : "16:9";
  CANVAS = c;
  W = SLIDE_SIZES[c].W;
  H = SLIDE_SIZES[c].H;
}

/** canvasWidth / canvasHeight saved with the slides of the current canvas. */
export const slideSize = () => ({ w: SLIDE_SIZES[CANVAS].w, h: SLIDE_SIZES[CANVAS].h });
export const PX_PER_SEC = 116;
export const SLIDE_PX = 1160; // 10 seconds

export const FONT = {
  fontName: "Poppins",
  fontFamily: "'Poppins', sans-serif",
  googleFontUrl: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap",
};

const sec = (s) => Math.round(s * PX_PER_SEC);
const MS_PER_PX = 1000 / PX_PER_SEC;

// "hh:mm:ss:mmm" - the editor reads a layer's place on the timeline from these on load.
function clock(ms) {
  const t = Math.max(0, Math.round(ms));
  const pad = (n, l = 2) => String(n).padStart(l, "0");
  return `${pad(Math.floor(t / 3600000))}:${pad(Math.floor(t / 60000) % 60)}:${pad(Math.floor(t / 1000) % 60)}:${pad(t % 1000, 3)}`;
}
export const rid = () => Math.random().toString(36).slice(2, 9);
const b64 = (s) => window.btoa(unescape(encodeURIComponent(s)));
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// ------------------------------------------------------------------------- timing / animation

/** Timeline fields: the layer appears at `at` seconds with the `in` effect (animate.css name). */
function timing(o = {}) {
  const st = sec(o.at || 0);
  const width = SLIDE_PX - st;
  return {
    startTime: clock(st * MS_PER_PX),
    duration: clock(width * MS_PER_PX),
    endTime: clock((st + width) * MS_PER_PX),
    _StartPositionOnTimeline: st,
    startPositionOnTimeline: st,
    LastStartPositionOnTimeline: st,
    StartAudioTime: st,
    widthOnTimeline: width,
    startTransitionWidth: o.in ? sec(o.dur || 0.6) : 0,
    startTransitionLeft: 0,
    endTransitionWidth: 0,
    endTransitionLeft: SLIDE_PX,
    InEffect: o.in ? "animated " + o.in + " " : "none",
    OutEffect: "none",
    InClass: "",
    OutClass: "",
  };
}

const common = {
  Added: true,
  canvas: null,
  userFile: "xyz",
  EditorZoom: 100,
  TimelineZoom: 6,
  visible: true,
  toolbar: false,
  FurtherSetting: false,
  lock: false,
  layerlock: false,
  opc: 1,
  hidden: false,
  Editable: false,
  InMultiDrag: false,
  showDiv: false,
  DrapEndAnimatedText: false,
  speedOn: false,
  fastspeedOn: false,
  startTimeOnTimeline: false,
  LoadLottie: true,
  midTransitionWidth: 0,
  StartTimer: 0,
  ComingFromToolbar: true,
  player: true,
  isStartTalkingInTimeline: true,
  Animate: true,
  CurrentFrame: 0,
  rotateAngle: 0,
  waitSS: true,
  JSONValue: null,
};

function box(x, y, w, h, opacity = 1) {
  return {
    x, y, width: w, height: h,
    originalX: x, originalY: y, originalWidth: w, originalHeight: h,
    opacity, originalOpacity: opacity,
  };
}

// ------------------------------------------------------------------------- SVG shapes

export const svg = (w, h, body, defs = "") =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none"><defs>${defs}</defs>${body}</svg>`;

export const lg = (id, c1, c2, x2 = 1, y2 = 1) =>
  `<linearGradient id="${id}" x1="0" y1="0" x2="${x2}" y2="${y2}"><stop offset="0" stop-color="${c1}"/><stop offset="1" stop-color="${c2}"/></linearGradient>`;

/** A shape layer from SVG markup. Gradient / filter ids "g" and "b" are made unique per layer. */
export function shape(markup, x, y, w, h, o = {}) {
  const id = rid();
  const content = markup
    .replace(/id="(g|b)"/g, (m, a) => `id="${a}${id}"`)
    .replace(/url\(#(g|b)\)/g, (m, a) => `url(#${a}${id})`);
  return {
    ...common,
    ...box(x, y, w, h, o.op ?? 1),
    ...timing(o),
    AnimationId: id,
    type: "basicShape",
    bgColor: "#838383",
    Name: o.name || "shape-" + id,
    video: null,
    volume: null,
    typeIcon: "vrpano",
    STdiff: null,
    angle: 0,
    svgContent: content,
    convertedBasicShape: "data:image/svg+xml;base64," + b64(content),
    svgURL: null,
    crop: "false",
    flip: "scaleX(1)",
    CanvasZoom: 100,
    ImageBlob: null,
    transparency: false,
    textColor: null,
    stroke: 0,
    strokeColor: null,
    ColorsFound: [],
    showColor: true,
    originalRotateAngle: 0,
    isTTS: false,
  };
}

export const bg = (c1, c2, name = "background") =>
  shape(svg(W, H, `<rect width="${W}" height="${H}" fill="url(#g)"/>`, lg("g", c1, c2)), 0, 0, W, H, { name });

export const rect = (x, y, w, h, fill, o = {}) =>
  shape(
    svg(w, h, `<rect x="0" y="0" width="${w}" height="${h}" rx="${o.r ?? 12}" fill="${o.grad ? "url(#g)" : fill}"/>`,
      o.grad ? lg("g", o.grad[0], o.grad[1], o.gx ?? 1, o.gy ?? 1) : ""),
    x, y, w, h, o
  );

/** White card with a soft shadow (and an optional coloured top strip). */
export function card(x, y, w, h, fill, o = {}) {
  const p = 8;
  const Wd = w + 2 * p;
  const Hd = h + 2 * p;
  const r = o.r ?? 14;
  return shape(
    svg(Wd, Hd,
      `<rect x="${p}" y="${p + 2}" width="${w}" height="${h}" rx="${r}" fill="${o.shadow || "#0b1f4b"}" opacity="0.13" filter="url(#b)"/>` +
      `<rect x="${p}" y="${p}" width="${w}" height="${h}" rx="${r}" fill="${fill}"/>` +
      (o.top ? `<rect x="${p}" y="${p}" width="${w}" height="5" rx="2" fill="${o.top}"/>` : ""),
      `<filter id="b" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="4"/></filter>`),
    x - p, y - p, Wd, Hd, o
  );
}

export const circle = (cx, cy, r, fill, o = {}) =>
  shape(svg(2 * r, 2 * r, `<circle cx="${r}" cy="${r}" r="${r}" fill="${o.grad ? "url(#g)" : fill}"/>`,
    o.grad ? lg("g", o.grad[0], o.grad[1]) : ""), cx - r, cy - r, 2 * r, 2 * r, o);

export const ring = (cx, cy, r, stroke, sw, o = {}) =>
  shape(svg(2 * r, 2 * r, `<circle cx="${r}" cy="${r}" r="${r - sw / 2}" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`),
    cx - r, cy - r, 2 * r, 2 * r, o);

export function dots(x, y, cols, rows, gap, color, o = {}) {
  let b = "";
  for (let i = 0; i < cols; i++) for (let j = 0; j < rows; j++) b += `<circle cx="${i * gap + 2}" cy="${j * gap + 2}" r="1.8" fill="${color}"/>`;
  const w = (cols - 1) * gap + 4;
  const h = (rows - 1) * gap + 4;
  return shape(svg(w, h, b), x, y, w, h, o);
}

const BLOB = "M43.6,-58.7C55.8,-50.6,64.1,-36.4,69.3,-20.7C74.5,-5,76.6,12.1,70.5,25.7C64.4,39.3,50.1,49.5,35.2,57.7C20.3,65.9,4.8,72.2,-11.7,72.6C-28.2,73,-45.6,67.5,-56.9,55.9C-68.2,44.3,-73.4,26.6,-73.9,9.3C-74.4,-8,-70.2,-24.9,-60.4,-37.6C-50.6,-50.3,-35.2,-58.8,-20.1,-65.4C-5,-72,9.9,-76.7,23.9,-73.1C37.9,-69.5,31.4,-66.8,43.6,-58.7Z";
export const blob = (x, y, w, h, fill, o = {}) =>
  shape(svg(200, 200, `<path d="${BLOB}" transform="translate(100 100)" fill="${o.grad ? "url(#g)" : fill}"/>`,
    o.grad ? lg("g", o.grad[0], o.grad[1]) : ""), x, y, w, h, o);

/** Icon drawings inside a circle (s = circle size). Names match AIPlanSpec.Icons. */
function iconBody(kind, s, fg, bgc) {
  const r = s / 2;
  const k = (v) => +(s * v).toFixed(2);
  const sw = (v) => `stroke="${fg}" stroke-width="${k(v)}" stroke-linecap="round" stroke-linejoin="round" fill="none"`;
  switch (kind) {
    case "check":
      return `<path d="M${k(.3)} ${k(.52)} L${k(.45)} ${k(.66)} L${k(.72)} ${k(.36)}" ${sw(.09)}/>`;
    case "star":
      return `<path d="M${r} ${k(.2)} L${k(.6)} ${k(.42)} L${k(.82)} ${k(.44)} L${k(.65)} ${k(.58)} L${k(.71)} ${k(.8)} L${r} ${k(.68)} L${k(.29)} ${k(.8)} L${k(.35)} ${k(.58)} L${k(.18)} ${k(.44)} L${k(.4)} ${k(.42)} Z" fill="${fg}"/>`;
    case "chart":
      return `<rect x="${k(.26)}" y="${k(.52)}" width="${k(.12)}" height="${k(.22)}" rx="2" fill="${fg}"/><rect x="${k(.44)}" y="${k(.38)}" width="${k(.12)}" height="${k(.36)}" rx="2" fill="${fg}"/><rect x="${k(.62)}" y="${k(.26)}" width="${k(.12)}" height="${k(.48)}" rx="2" fill="${fg}"/>`;
    case "bulb":
      return `<circle cx="${r}" cy="${k(.42)}" r="${k(.17)}" fill="${fg}"/><rect x="${k(.42)}" y="${k(.58)}" width="${k(.16)}" height="${k(.14)}" rx="2" fill="${fg}"/>`;
    case "rocket":
      return `<path d="M${r} ${k(.18)} C${k(.66)} ${k(.28)} ${k(.66)} ${k(.5)} ${k(.6)} ${k(.66)} L${k(.4)} ${k(.66)} C${k(.34)} ${k(.5)} ${k(.34)} ${k(.28)} ${r} ${k(.18)} Z" fill="${fg}"/><circle cx="${r}" cy="${k(.42)}" r="${k(.06)}" fill="${bgc}"/><path d="M${k(.42)} ${k(.7)} L${r} ${k(.84)} L${k(.58)} ${k(.7)} Z" fill="${fg}" opacity=".7"/>`;
    case "globe":
      return `<circle cx="${r}" cy="${r}" r="${k(.24)}" ${sw(.05)}/><ellipse cx="${r}" cy="${r}" rx="${k(.1)}" ry="${k(.24)}" ${sw(.04)}/><line x1="${k(.26)}" y1="${r}" x2="${k(.74)}" y2="${r}" ${sw(.04)}/>`;
    case "phone":
      return `<path d="M${k(.32)} ${k(.24)} L${k(.42)} ${k(.24)} L${k(.47)} ${k(.38)} L${k(.4)} ${k(.44)} C${k(.46)} ${k(.56)} ${k(.52)} ${k(.6)} ${k(.58)} ${k(.64)} L${k(.64)} ${k(.57)} L${k(.78)} ${k(.62)} L${k(.78)} ${k(.72)} C${k(.5)} ${k(.76)} ${k(.26)} ${k(.52)} ${k(.32)} ${k(.24)} Z" fill="${fg}"/>`;
    case "mail":
      return `<rect x="${k(.24)}" y="${k(.32)}" width="${k(.52)}" height="${k(.36)}" rx="3" ${sw(.05)}/><path d="M${k(.26)} ${k(.35)} L${r} ${k(.52)} L${k(.74)} ${k(.35)}" ${sw(.05)}/>`;
    case "heart":
      return `<path d="M${r} ${k(.74)} C${k(.2)} ${k(.5)} ${k(.24)} ${k(.26)} ${k(.4)} ${k(.28)} C${k(.46)} ${k(.29)} ${r} ${k(.36)} ${r} ${k(.36)} C${r} ${k(.36)} ${k(.54)} ${k(.29)} ${k(.6)} ${k(.28)} C${k(.76)} ${k(.26)} ${k(.8)} ${k(.5)} ${r} ${k(.74)} Z" fill="${fg}"/>`;
    case "users":
      return `<circle cx="${k(.4)}" cy="${k(.38)}" r="${k(.1)}" fill="${fg}"/><path d="M${k(.22)} ${k(.7)} C${k(.22)} ${k(.52)} ${k(.58)} ${k(.52)} ${k(.58)} ${k(.7)} Z" fill="${fg}"/><circle cx="${k(.64)}" cy="${k(.42)}" r="${k(.08)}" fill="${fg}" opacity=".7"/><path d="M${k(.52)} ${k(.7)} C${k(.52)} ${k(.56)} ${k(.8)} ${k(.56)} ${k(.8)} ${k(.7)} Z" fill="${fg}" opacity=".7"/>`;
    case "shield":
      return `<path d="M${r} ${k(.2)} L${k(.74)} ${k(.3)} L${k(.74)} ${k(.48)} C${k(.74)} ${k(.64)} ${k(.62)} ${k(.74)} ${r} ${k(.8)} C${k(.38)} ${k(.74)} ${k(.26)} ${k(.64)} ${k(.26)} ${k(.48)} L${k(.26)} ${k(.3)} Z" fill="${fg}"/><path d="M${k(.39)} ${k(.5)} L${k(.47)} ${k(.58)} L${k(.62)} ${k(.42)}" stroke="${bgc}" stroke-width="${k(.05)}" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`;
    case "clock":
      return `<circle cx="${r}" cy="${r}" r="${k(.25)}" ${sw(.06)}/><path d="M${r} ${k(.36)} L${r} ${r} L${k(.6)} ${k(.58)}" ${sw(.06)}/>`;
    case "tag":
      return `<path d="M${k(.24)} ${k(.3)} L${k(.24)} ${k(.48)} L${k(.52)} ${k(.76)} L${k(.76)} ${k(.52)} L${k(.48)} ${k(.24)} L${k(.3)} ${k(.24)} Z" fill="${fg}"/><circle cx="${k(.36)}" cy="${k(.36)}" r="${k(.05)}" fill="${bgc}"/>`;
    case "cart":
      return `<path d="M${k(.22)} ${k(.28)} L${k(.32)} ${k(.28)} L${k(.4)} ${k(.6)} L${k(.7)} ${k(.6)} L${k(.76)} ${k(.38)} L${k(.35)} ${k(.38)}" ${sw(.06)}/><circle cx="${k(.43)}" cy="${k(.71)}" r="${k(.05)}" fill="${fg}"/><circle cx="${k(.67)}" cy="${k(.71)}" r="${k(.05)}" fill="${fg}"/>`;
    case "leaf":
      return `<path d="M${k(.26)} ${k(.74)} C${k(.24)} ${k(.42)} ${k(.42)} ${k(.24)} ${k(.76)} ${k(.24)} C${k(.76)} ${k(.56)} ${k(.58)} ${k(.76)} ${k(.26)} ${k(.74)} Z" fill="${fg}"/><path d="M${k(.3)} ${k(.7)} L${k(.6)} ${k(.4)}" stroke="${bgc}" stroke-width="${k(.04)}" stroke-linecap="round"/>`;
    case "home":
      return `<path d="M${k(.22)} ${k(.5)} L${r} ${k(.24)} L${k(.78)} ${k(.5)} L${k(.7)} ${k(.5)} L${k(.7)} ${k(.76)} L${k(.3)} ${k(.76)} L${k(.3)} ${k(.5)} Z" fill="${fg}"/><rect x="${k(.44)}" y="${k(.58)}" width="${k(.12)}" height="${k(.18)}" fill="${bgc}"/>`;
    case "gift":
      return `<rect x="${k(.24)}" y="${k(.42)}" width="${k(.52)}" height="${k(.34)}" rx="2" fill="${fg}"/><rect x="${k(.2)}" y="${k(.32)}" width="${k(.6)}" height="${k(.12)}" rx="2" fill="${fg}"/><rect x="${k(.46)}" y="${k(.32)}" width="${k(.08)}" height="${k(.44)}" fill="${bgc}"/><path d="M${r} ${k(.32)} C${k(.4)} ${k(.18)} ${k(.3)} ${k(.26)} ${r} ${k(.32)} C${k(.6)} ${k(.18)} ${k(.7)} ${k(.26)} ${r} ${k(.32)} Z" ${sw(.04)}/>`;
    case "calendar":
      return `<rect x="${k(.24)}" y="${k(.28)}" width="${k(.52)}" height="${k(.48)}" rx="3" ${sw(.05)}/><line x1="${k(.24)}" y1="${k(.42)}" x2="${k(.76)}" y2="${k(.42)}" ${sw(.05)}/><circle cx="${k(.38)}" cy="${k(.55)}" r="${k(.035)}" fill="${fg}"/><circle cx="${r}" cy="${k(.55)}" r="${k(.035)}" fill="${fg}"/><circle cx="${k(.62)}" cy="${k(.55)}" r="${k(.035)}" fill="${fg}"/><circle cx="${k(.38)}" cy="${k(.66)}" r="${k(.035)}" fill="${fg}"/>`;
    default:
      return iconBody("check", s, fg, bgc);
  }
}

export const icon = (kind, cx, cy, r, bgc, fg, o = {}) => {
  const s = 2 * r;
  return shape(svg(s, s, `<circle cx="${r}" cy="${r}" r="${r}" fill="${bgc}"/>${iconBody(kind, s, fg, bgc)}`), cx - r, cy - r, s, s, o);
};

export function stars(x, y, n, size, color, o = {}) {
  let b = "";
  for (let i = 0; i < n; i++) {
    b += `<path transform="translate(${i * (size + 4)} 0) scale(${size / 24})" d="M12 1.5l3.1 6.6 7.2.9-5.3 5 1.4 7.1L12 17.6 5.6 21.1 7 14l-5.3-5 7.2-.9z" fill="${color}"/>`;
  }
  const w = n * (size + 4) - 4;
  return shape(svg(w, size, b), x, y, w, size, o);
}

export function burst(cx, cy, r, fill, o = {}) {
  const n = 16;
  let pts = "";
  for (let i = 0; i < 2 * n; i++) {
    const a = (Math.PI * i) / n;
    const rr = i % 2 ? r * 0.82 : r;
    pts += `${(r + rr * Math.cos(a)).toFixed(1)},${(r + rr * Math.sin(a)).toFixed(1)} `;
  }
  return shape(svg(2 * r, 2 * r, `<polygon points="${pts}" fill="${fill}"/>`), cx - r, cy - r, 2 * r, 2 * r, o);
}

export const quoteMark = (x, y, size, color, o = {}) =>
  shape(svg(40, 32, `<path d="M0 32 V18 C0 8 6 2 16 0 L17 5 C11 7 9 11 9 15 H16 V32 Z M23 32 V18 C23 8 29 2 39 0 L40 5 C34 7 32 11 32 15 H39 V32 Z" fill="${color}"/>`),
    x, y, size, Math.round(size * 0.8), o);

/** A phone showing an app screen (used when a scene has no presenter). */
export function phone(x, y, w, c1, c2, o = {}) {
  const h = Math.round(w * 2);
  return shape(
    svg(100, 200,
      `<rect x="2" y="2" width="96" height="196" rx="16" fill="#111827"/><rect x="7" y="9" width="86" height="182" rx="11" fill="url(#g)"/>` +
      `<rect x="38" y="13" width="24" height="5" rx="2.5" fill="#111827"/><circle cx="50" cy="78" r="18" fill="#fff" opacity=".9"/>` +
      `<rect x="22" y="110" width="56" height="7" rx="3.5" fill="#fff" opacity=".9"/><rect x="28" y="124" width="44" height="5" rx="2.5" fill="#fff" opacity=".6"/>` +
      `<rect x="26" y="150" width="48" height="16" rx="8" fill="#fff"/>`,
      lg("g", c1, c2)),
    x, y, w, h, o
  );
}

// ------------------------------------------------------------------------- text

let measureCtx = null;
function ctx() {
  if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
  return measureCtx;
}

function textWidth(text, size, weight, ls) {
  const c = ctx();
  c.font = `${weight} ${size}px Poppins, sans-serif`;
  // Letter spacing is added per character, as the browser does.
  return c.measureText(text).width + Math.max(0, text.length - 1) * ls * size;
}

/** Width of a pill button that fits its label. */
export function buttonWidth(label, size = 12, min = 110, max = 200) {
  return Math.round(Math.min(max, Math.max(min, textWidth(String(label || ""), size, "700", 0) + 44)));
}

/** Splits the text into lines that fit `width` (words are never split unless one is too long). */
export function wrap(text, width, size, weight = "400", ls = 0) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  // A little slack, so the editor (which lays text out itself) does not wrap one word earlier.
  const max = width * 0.95;
  for (const word of words) {
    const tryLine = line ? line + " " + word : word;
    if (!line || textWidth(tryLine, size, weight, ls) <= max) line = tryLine;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Picks the largest font size (from `size` down to `min`) at which the text fits in `maxLines`
 * lines of `width`. Text that still does not fit is shortened with "...".
 */
export function fit(text, { width, size, min = Math.max(8, Math.round(size * 0.6)), maxLines = 1, weight = "400", ls = 0 }) {
  let s = size;
  let lines = wrap(text, width, s, weight, ls);
  while (lines.length > maxLines && s > min) {
    s -= 1;
    lines = wrap(text, width, s, weight, ls);
  }
  let out = String(text || "").trim();
  if (lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    let last = lines[maxLines - 1];
    while (last.length > 1 && textWidth(last + "...", s, weight, ls) > width * 0.95) last = last.slice(0, -1).trimEnd();
    lines[maxLines - 1] = last + "...";
    out = lines.join(" ");
  }
  return { text: out, size: s, lines: lines.length };
}

/** A text layer. o: weight, align, lh (line height), ls (letter spacing, em), lines, in, at. */
export function text(txt, x, y, w, size, color, o = {}) {
  const id = rid();
  const lh = o.lh || 1.15;
  const lines = o.lines || 1;
  const h = Math.round(size * lh * lines + 4);
  return {
    ...common,
    ...box(x, y, w, h, 1),
    ...timing(o),
    AnimationId: id,
    TextId: rid(),
    type: "text",
    spanHeight: "",
    spanWidth: "",
    scale: 1,
    bgColor: "#323232",
    Name: o.name || "text-" + id,
    typeIcon: "text_fields",
    transform: "translate(0px, 0px)",
    text: txt,
    originalText: txt,
    edit: "false",
    fontSize: size,
    originalFontSize: size,
    Align: o.align || "left",
    fontWeight: o.weight || "700",
    Fontstyle: "calibri",
    textDec: "none",
    LineHeight: lh,
    LetterSpacing: o.ls || 0,
    textColor: color,
    googleFontSetting: FONT,
    isFontButtonClick: false,
  };
}

/** Fitted text: font size and line count chosen so it fits the box. Returns [layer, height]. */
export function fitText(txt, x, y, w, size, color, o = {}) {
  const weight = o.weight || "700";
  const f = fit(txt, { width: w, size, min: o.min, maxLines: o.maxLines || 1, weight, ls: o.ls || 0 });
  const layer = text(f.text, x, y, w, f.size, color, { ...o, lines: f.lines });
  return [layer, layer.height];
}

/**
 * Dark wash over a library background so white text stays readable. side: "left" (fades out to the
 * right, for text on the left), or "full" (even tint).
 */
export function wash(color, opacity, side = "left", o = {}) {
  const body =
    side === "full"
      ? `<rect width="${W}" height="${H}" fill="${color}" opacity="${opacity}"/>`
      : `<rect width="${W}" height="${H}" fill="url(#g)"/>`;
  const defs =
    side === "full"
      ? ""
      : `<linearGradient id="g" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${color}" stop-opacity="${opacity}"/><stop offset="0.55" stop-color="${color}" stop-opacity="${opacity * 0.75}"/><stop offset="0.85" stop-color="${color}" stop-opacity="0"/></linearGradient>`;
  return shape(svg(W, H, body, defs), 0, 0, W, H, { name: "background wash", ...o });
}

// ------------------------------------------------------------------------- props

/** An animated prop from the library (Lottie), fitted into the box (x, y, w, h) keeping its shape. */
export function prop(p, animation, x, y, w, h, o = {}) {
  const ratio = animation && animation.w && animation.h ? animation.h / animation.w : 1;
  let pw = w;
  let ph = w * ratio;
  if (ph > h) {
    ph = h;
    pw = h / ratio;
  }
  const px = Math.round(x + (w - pw) / 2);
  const py = Math.round(y + (h - ph)); // standing on the bottom of the box
  const id = rid();
  return {
    ...common,
    ...box(px, py, Math.round(pw), Math.round(ph), 1),
    ...timing(o),
    AnimationId: id,
    type: "Props",
    Subtype: "",
    bgColor: "#8e5fff",
    Name: "Prop-" + (p.name || id),
    AnimationPath: p.file,
    PropImage: p.image,
    typeIcon: "vrpano",
    angle: 0,
    flip: "scaleX(1)",
    JSONValue: animation || null,
  };
}

// ------------------------------------------------------------------------- library shapes and photos

/** Fits a w:h ratio (height / width) into a box, centered, standing on the box's bottom. */
function fitBox(ratio, x, y, w, h) {
  let pw = w;
  let ph = w * ratio;
  if (ph > h) {
    ph = h;
    pw = h / ratio;
  }
  return { x: Math.round(x + (w - pw) / 2), y: Math.round(y + (h - ph)), w: Math.round(pw), h: Math.round(ph) };
}

/** A static illustration from the Shapes panel (library SVG), fitted in the box keeping its shape. */
export function libShape(item, ratio, x, y, w, h, o = {}) {
  const b = fitBox(ratio || 1, x, y, w, h);
  const id = rid();
  return {
    ...common,
    ...box(b.x, b.y, b.w, b.h, 1),
    ...timing(o),
    AnimationId: id,
    type: "Shape",
    bgColor: "#838383",
    Name: "Shape-" + (item.name || id),
    video: null,
    volume: null,
    typeIcon: "vrpano",
    angle: 0,
    ShapePath: item.path,
    crop: "false",
    flip: "scaleX(1)",
  };
}

/** A photo (image layer) filling the box; src is the picture's address on our server. */
export function photo(src, x, y, w, h, o = {}) {
  const id = rid();
  return {
    ...common,
    ...box(Math.round(x), Math.round(y), Math.round(w), Math.round(h), 1),
    ...timing(o),
    AnimationId: id,
    type: "image",
    bgColor: "#554bf5",
    Name: o.name || "photo-" + id,
    video: null,
    volume: null,
    typeIcon: "vrpano",
    STdiff: null,
    angle: 0,
    ImageSrc: src,
    ImageBlob: null,
    crop: "false",
    flip: "scaleX(1)",
  };
}

/** Changes how long the layer stays on the timeline (it appears at `at` and is shown for `seconds`). */
export function shortenTo(layer, seconds) {
  const st = layer.startPositionOnTimeline || 0;
  const width = Math.max(1, Math.min(SLIDE_PX - st, sec(seconds)));
  layer.widthOnTimeline = width;
  layer.duration = clock(width * MS_PER_PX);
  layer.endTime = clock((st + width) * MS_PER_PX);
  layer.endTransitionLeft = width;
  return layer;
}

// ------------------------------------------------------------------------- A to Z moves

/**
 * How a prop or illustration comes in (AIPlanSpec.Motions), using the editor's A to Z move
 * (layer.Motion, Utils/layerMotion.js): the layer starts at A and travels to Z, its place in the
 * design. Returns { x, y } (A, where the layer is placed), the Motion (or null) and the entry
 * effect to use (or null when the move is the entrance).
 *   pop    no move, zooms in            drop   falls in from above
 *   fly    flies in on a curve          bounce bounces in from the side
 *   roll   rolls in from the side       float  bobs gently in place for the whole scene
 */
export function entrance(kind, x, y, w, h) {
  const Z = { toXf: x / W, toYf: y / H };
  const fromRight = x + w / 2 > W / 2;
  const sideX = fromRight ? W + 8 : -w - 8;
  switch (kind) {
    case "drop":
      return { x, y: -h - 8, motion: { ...Z, duration: 0.9, easing: "smooth" }, in: null };
    case "fly": {
      const ay = Math.max(-h, y - 60);
      const midX = (sideX + x) / 2;
      const top = Math.max(0, Math.min(ay, y) - Math.max(50, Math.abs(x - sideX) * 0.3));
      return { x: sideX, y: ay, motion: { ...Z, duration: 1.2, easing: "smooth", path: "arc", hXf: midX / W, hYf: top / H }, in: null };
    }
    case "bounce": {
      const hopX = sideX + (x - sideX) * 0.25;
      return { x: sideX, y, motion: { ...Z, duration: 1.4, easing: "linear", path: "bounce", bounces: 3, bounciness: 0.55, hXf: hopX / W, hYf: Math.max(0, y - 60) / H }, in: null };
    }
    case "roll":
      return { x: sideX, y, motion: { ...Z, duration: 1.2, easing: "smooth", spin: fromRight ? -1.5 : 1.5 }, in: null };
    case "float":
      return { x, y, motion: { toXf: x / W, toYf: (y - 7) / H, duration: 1.6, easing: "smooth", repeat: "pingpong", times: 0, pause: 0 }, in: "zoomIn" };
    default:
      return { x, y, motion: null, in: "zoomIn" };
  }
}

/** Adds an A to Z move to the layer. */
export function withMotion(layer, motion) {
  if (motion) layer.Motion = { enabled: true, delay: 0, easing: "linear", ...motion };
  return layer;
}

/** Where a layer with a move ends (Z), or where it is placed when it has none (or goes back and forth). */
export function restingPlace(layer) {
  const m = layer && layer.Motion;
  if (!m || m.enabled === false || m.repeat === "pingpong" || m.repeat === "loop") return { x: layer.x, y: layer.y };
  return { x: Number(m.toXf) * W, y: Number(m.toYf) * H };
}

// ------------------------------------------------------------------------- character

/** A presenter (library character) layer; JSONValue (its animation) is added by the builder. */
export function character(p, x, y, o = {}) {
  const id = rid();
  const sc = o.scale || 1;
  const w = Math.round(p.w * sc);
  const h = Math.round(p.h * sc);
  return {
    ...common,
    ...box(x, y, w, h, 1),
    ...timing(o),
    AnimationId: id,
    type: "Character",
    bgColor: "#00adff",
    Name: o.name || "Presenter",
    typeIcon: "person",
    character_poses: p.poses,
    ImageSrc: p.image,
    character_FilePath: p.file,
    CharacterStop: true,
    CharacterPause: false,
    CharacterPlayed: false,
    flip: o.flip ? "scaleX(-1)" : "scaleX(1)",
    AzData: null,
    startAtoZ: false,
    ImageFrame: 0,
    isPlaying: false,
    OriginalJSONValue: null,
    midTransitionWidth: null,
  };
}

export { esc };
